// « Ajouter un connecteur » of the office's library, shared by « Connecteurs du bureau » (a section
// of the page) and Administration → Connecteurs (AJOUTER UN CONNECTEUR: openAddConnector, a modal):
// one card per catalogue service — Canva, and the plugins the office serves itself, Shopify and
// Mailgun — whose AJOUTER opens its add window (myconnectors/plugin.js openCatalogAdd: a name,
// proposed « Canva », then « Canva 2 »…, and the service's fields), and « Autre service (adresse
// MCP) », open from the start (a name and an https address, checked here like the server does:
// POST {label, url}). A service is added as many times as there are accounts, each under its own
// name (the office refuses a name already used): a card says which ones the office already has. A
// plugin needs the office's MCP server: off, its card says why and cannot add. The limits (the
// office's library, a member's own additions) are said and stop the buttons. Google Drive needs the
// office's Google app: until an administrator sets it, its card says so (an administrator gets a
// shortcut to Administration → Connecteurs). A member's addition
// is allowed for him at once by the server (his « Connecteurs autorisés »): his `me` is read
// again. The new connector goes back through `onAdded` (the caller opens its connect window when
// it still has to be connected).
import { h, button, input, field, icon, uid, spinner } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { iconForConnector } from '../connectoricons.js';
import { errorText } from '../admin/model.js';
import { formError, setBusy, alertBox } from '../admin/common.js';
import { WRITE_TIMEOUT_MS } from './actions.js';
import {
  connectorOf, normalizeLibrary, validateCustomConnector, addLimit, LABEL_MAX, URL_MAX, LIBRARY_BASE,
} from './model.js';
import { openCatalogAdd } from './plugin.js';
import { GDRIVE_ID } from './drive-model.js';
import { childLayers } from '../automations/widgets.js';
import './strings.js';

/** The office's own host is refused as a connector address (it would call itself). */
function officeHosts() {
  return [globalThis.location?.hostname, globalThis.location?.host].filter(Boolean);
}

/**
 * After a member's addition the server allowed it for him (his policy changed): `me` again.
 * Never throws; nothing for an administrator (never restricted).
 * @param {object} ctx
 */
export async function refreshMe(ctx) {
  if (ctx.store.get().me?.role === 'admin') return;
  try {
    const res = await ctx.api.get('/api/auth/me');
    if (res?.user && typeof res.user === 'object' && res.user.id === ctx.store.get().me?.id) ctx.store.set({ me: res.user });
  } catch { /* the next bootstrap brings it */ }
}

/**
 * Creates the panel.
 * @param {object} ctx app context
 * @param {{onAdded?: (c: object) => void, isOpen?: () => boolean, idPrefix?: string}} [opts] onAdded: the new
 *   connector (normalised, status « added »); isOpen: false once the page is gone
 * @returns {{el: HTMLElement, update(next: {data: object|null, list: object[]}): void, focus(): void, reset(): void}}
 */
export function createAddPanel(ctx, { onAdded = () => {}, isOpen = () => true, idPrefix = 'pc-add' } = {}) {
  let data = null; // normalizeLibrary()
  let list = [];   // the whole library (connectorOf records)
  let adding = false;
  const windows = childLayers(null);
  const fk = (el, key) => { el.dataset.fk = key; return el; };

  // The « Autre service » form is built once, so a re-render (live event, minute tick) keeps what
  // is typed and where the focus is.
  const labelInput = fk(input({ name: 'pc-label', maxLength: LABEL_MAX, placeholder: t('pc.custom.labelPlaceholder'), autocomplete: 'off' }), 'pc-label');
  const urlInput = fk(input({
    name: 'pc-url', maxLength: URL_MAX, placeholder: t('pc.custom.urlPlaceholder'), autocomplete: 'off', autocapitalize: 'none',
    spellcheck: false, inputmode: 'url',
  }), 'pc-url');
  const labelField = field(t('pc.custom.label'), labelInput);
  const urlField = field(t('pc.custom.url'), urlInput, { hint: t('pc.custom.urlHint') });
  const customError = formError();
  const customSubmit = fk(button(t('pc.custom.submit'), { variant: 'primary', size: 'sm', icon: 'plus', type: 'submit' }), 'pc-custom-submit');
  const customForm = h('form', { class: 'pc-custom-form', novalidate: true, onSubmit: (e) => { e?.preventDefault?.(); addCustom(); } },
    h('p', { class: 'pc-custom-intro' }, t('pc.custom.intro')),
    labelField, urlField, customError, h('div', { class: 'pc-custom-foot' }, customSubmit));
  const custom = h('details', { class: 'pc-custom', open: true },
    h('summary', { class: 'pc-custom-summary' }, icon('plus', { size: 14 }), h('span', {}, t('pc.custom.title'))), customForm);

  const titleId = uid(idPrefix);
  const introEl = h('p', { class: 'pc-add-intro' });
  const limitEl = h('p', { class: 'pc-limit', role: 'status', hidden: true });
  const catalogueHost = h('div', { class: 'pc-catalogue' });
  const el = h('section', { class: 'pc-section pc-add', 'aria-labelledby': titleId },
    h('h3', { class: 'pc-section-title', id: titleId }, h('span', {}, t('pc.add.title'))),
    introEl, limitEl, catalogueHost, custom);

  const limit = () => (data ? addLimit(data.limits, list) : null);

  function catalogueCard(c) {
    const inOffice = list.filter((x) => x.catalogId === c.id);
    const full = !!limit();
    // Google Drive without the office's Google app: an administrator's way to set it.
    const googleApp = c.id === GDRIVE_ID && !c.available && data?.apps?.google?.ready === false && ctx.store.get().me?.role === 'admin'
      ? fk(button(t('pc.drive.noAppAdmin'), { variant: 'ghost', size: 'sm', icon: 'settings', class: 'pc-cat-go', onClick: () => ctx.navigate?.('#/admin/connectors') }), 'cat-google-app')
      : null;
    return h('article', { class: ['pc-cat', { 'is-unavailable': !c.available }], dataset: { cat: c.id } },
      iconForConnector({ key: c.id, name: c.label }, { size: 32 }),
      h('div', { class: 'pc-cat-text' },
        h('h4', { class: 'pc-cat-name' }, c.label),
        c.description ? h('p', { class: 'pc-cat-desc' }, c.description) : null,
        c.host ? h('p', { class: 'pc-cat-host mono-label' }, c.host) : null,
        inOffice.length ? h('p', { class: 'pc-cat-others' }, t(c.id === GDRIVE_ID ? 'pc.add.inOffice.gdrive' : 'pc.add.inOffice', { names: inOffice.map((x) => x.label).join(', ') })) : null,
        !c.available && c.unavailable ? h('p', { class: 'pc-cat-off' }, c.unavailable) : null,
        googleApp),
      fk(button(t('pc.add.button'), {
        variant: 'primary', size: 'sm', icon: 'plus', ariaLabel: t('pc.add.buttonAria', { name: c.label }),
        disabled: adding || full || !c.available, onClick: () => addCatalog(c),
      }), `cat:${c.id}`));
  }

  function render() {
    const member = ctx.store.get().me?.role !== 'admin';
    introEl.textContent = member ? `${t('pc.add.intro')} ${t('pc.add.introMember')}` : t('pc.add.intro');
    const lim = limit();
    limitEl.hidden = !lim;
    limitEl.textContent = lim ? t(lim.kind === 'office' ? 'pc.add.limitOffice' : 'pc.add.limitMember', { max: lim.max }) : '';
    customSubmit.disabled = adding || !!lim;
    if (!data) {
      catalogueHost.hidden = false;
      catalogueHost.replaceChildren(h('div', { class: 'pc-state' }, spinner(t('pc.loading')), h('span', { class: 'muted' }, t('pc.loading'))));
      return;
    }
    catalogueHost.replaceChildren(...data.catalogue.map(catalogueCard));
    catalogueHost.hidden = !data.catalogue.length;
  }

  async function added(res) {
    const saved = connectorOf(res);
    if (!saved) return;
    await refreshMe(ctx);
    if (!isOpen()) return;
    ctx.toast.success(t('pc.add.added', { name: saved.label }));
    onAdded(saved);
  }

  /** AJOUTER on a card: its add window (a name, the service's fields); the toasts are its own. */
  function addCatalog(c) {
    if (adding || !isOpen()) return null;
    return windows.add(openCatalogAdd(ctx, c, {
      data, list,
      onAdded: async (saved) => {
        await refreshMe(ctx);
        if (isOpen()) onAdded(saved);
      },
    }));
  }

  async function addCustom() {
    if (adding || !isOpen()) return;
    const res = validateCustomConnector({ label: labelInput.value, url: urlInput.value }, {
      existing: list, catalogue: data?.catalogue || [], officeHosts: officeHosts(),
    });
    labelField.setError(res.errors.label || null);
    urlField.setError(res.errors.url || null);
    customError.set('');
    if (!res.ok) {
      (res.errors.label ? labelInput : urlInput).focus();
      return;
    }
    adding = true;
    setBusy(customSubmit, true);
    render();
    try {
      const out = await ctx.api.post(LIBRARY_BASE, res.value, { timeoutMs: WRITE_TIMEOUT_MS });
      labelInput.value = '';
      urlInput.value = '';
      if (isOpen()) await added(out);
    } catch (err) {
      if (!isOpen()) return;
      // The server's refusal of the address or the name (`details.field: 'url' | 'label'`) goes under its field.
      if (err?.details?.field === 'url') {
        urlField.setError(errorText(err));
        urlInput.focus();
      } else if (err?.details?.field === 'label') {
        labelField.setError(errorText(err));
        labelInput.focus();
      } else customError.set(errorText(err));
    } finally {
      adding = false;
      setBusy(customSubmit, false);
      if (isOpen()) render();
    }
  }

  render();
  return {
    el,
    update(next = {}) {
      if ('data' in next) data = next.data;
      if ('list' in next) list = Array.isArray(next.list) ? next.list : [];
      render();
    },
    /** Brings the panel into view, the name of « Autre service » focused. */
    focus() {
      custom.open = true;
      el.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
      labelInput.focus?.({ preventScroll: true });
    },
    reset() {
      labelInput.value = '';
      urlInput.value = '';
      labelField.setError(null);
      urlField.setError(null);
      customError.set('');
      windows.closeAll('route');
    },
  };
}

/**
 * AJOUTER UN CONNECTEUR (Administration → Connecteurs): the add panel in a modal, fed by GET
 * /api/connectors/library (catalogue, limits, the library for the duplicates); the feature off or
 * the person not allowed: the server's reason instead. The modal closes once a connector is added
 * and `onAdded` gets it (the caller opens its connect window).
 * @param {object} ctx app context
 * @param {{onAdded?: (c: object) => void}} [opts]
 * @returns {import('../overlay.js').Layer}
 */
export function openAddConnector(ctx, { onAdded = () => {} } = {}) {
  let layer = null;
  const isOpen = () => !!layer?.isOpen?.();
  const panel = createAddPanel(ctx, {
    isOpen,
    idPrefix: 'pc-addmodal',
    onAdded: (saved) => { layer?.close('done'); onAdded(saved); },
  });
  const body = h('div', { class: 'pc-addmodal' }, h('div', { class: 'pc-state' }, spinner(t('pc.loading')), h('span', { class: 'muted' }, t('pc.loading'))));
  const close = button(t('common.close'), { variant: 'ghost', onClick: () => layer?.close('button') });
  layer = ctx.overlay.openModal({
    title: t('pc.modal.title'), subtitle: t('pc.modal.subtitle'), size: 'md', content: body, footer: [close],
    className: 'adm-modal pc-modal pc-addmodal-layer',
  });
  layer.onClose?.(() => panel.reset());

  async function load() {
    try {
      const data = normalizeLibrary(await ctx.api.get(LIBRARY_BASE));
      if (!isOpen()) return;
      if (!data.enabled || !data.canAdd) {
        body.replaceChildren(alertBox('amber', data.message || (data.enabled ? t('pc.add.notAllowed') : t('pc.unavailable'))));
        return;
      }
      panel.update({ data, list: data.connectors });
      body.replaceChildren(panel.el);
    } catch (err) {
      if (!isOpen()) return;
      body.replaceChildren(alertBox('danger', errorText(err)),
        h('div', { class: 'pc-addmodal-retry' }, button(t('pc.retry'), { variant: 'secondary', size: 'sm', icon: 'refresh', onClick: () => load() })));
    }
  }
  load();
  return layer;
}
